import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X, ArrowRight, Sparkles, Brain, TrendingUp, FileText, Users } from 'lucide-react';
import { PollarLogoSVG } from '@/constants/logo';
import { trackPageView, trackClick, EventTypes } from '@/utils/analytics';

const SlidoAlternative = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('slido-alternative');
  }, []);

  const handleCTA = (location) => {
    trackClick(`slido-alternative-cta-${location}`, window.location.href);
    navigate('/login?signup=true');
  };

  const comparisonRows = [
    {
      feature: 'Analysis',
      standard: 'Simple Upvoting',
      pollar: 'AI Sentiment & Theme Clustering',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Continuity',
      standard: 'Reset after every meeting',
      pollar: 'Continuous Project Ideation',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Insights',
      standard: 'Exported List of Questions',
      pollar: 'AI-Generated Executive Summary',
      standardIcon: false,
      pollarIcon: true
    },
    {
      feature: 'Pricing',
      standard: 'Expensive "Pro" tiers for basic features',
      pollar: 'Full AI Suite Included',
      standardIcon: false,
      pollarIcon: true
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Slido Alternative for Actionable Q&A | Pollar</title>
        <meta 
          name="description" 
          content="Go beyond simple Q&A. Pollar is the Slido alternative that uses AI to group questions by sentiment and turn feedback into project roadmaps." 
        />
        <meta property="og:title" content="Best Slido Alternative for Actionable Q&A | Pollar" />
        <meta 
          property="og:description" 
          content="Go beyond simple Q&A. Pollar is the Slido alternative that uses AI to group questions by sentiment and turn feedback into project roadmaps." 
        />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL}/alternatives/slido-alternative-pollar`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <PollarLogoSVG className="w-10 h-10" />
                <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Pollar
                </span>
              </button>
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => handleCTA('nav')}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  Get Started Free
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Section A: Hero */}
        <section className="pt-20 pb-16 px-6">
          <div className="container mx-auto max-w-5xl text-center">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Brain className="w-4 h-4" />
              The Smarter Alternative to Slido Q&A
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Don't Just Collect Questions.{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Build Solutions.
              </span>
            </h1>

            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
                The Q&A Tool That Actually Does the Work.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Slido is great for asking questions, but Pollar is built for answering the "What's next?" 
                Pollar's AI analyzes every entry to identify patterns, sentiment, and priorities in real-time.
              </p>
            </div>

            <Button 
              size="lg"
              onClick={() => handleCTA('hero')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg px-8 py-6 h-auto"
            >
              Upgrade Your Meetings
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>

            <p className="text-sm text-gray-500 mt-4">
              AI-powered analysis • Sentiment detection • Executive summaries included
            </p>

            {/* Quick Value Props */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <Brain className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">AI Analysis</h3>
                <p className="text-sm text-gray-600">
                  Automatically cluster questions by theme and sentiment
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <TrendingUp className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Project Continuity</h3>
                <p className="text-sm text-gray-600">
                  Track conversations across multiple sessions
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <FileText className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Executive Reports</h3>
                <p className="text-sm text-gray-600">
                  Get AI-generated summaries ready for leadership
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section B: Comparison Table */}
        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Why Facilitators Choose Pollar</h2>
              <p className="text-xl text-gray-600">
                Go beyond Q&A into strategic insights
              </p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-6 px-6 text-left bg-gray-50 font-bold text-lg">
                      Feature
                    </th>
                    <th className="py-6 px-6 text-center bg-gray-100 text-gray-500 font-semibold">
                      Basic Q&A Tools
                      <div className="text-sm font-normal mt-1">(Slido)</div>
                    </th>
                    <th className="py-6 px-6 text-center bg-gradient-to-r from-indigo-50 to-purple-50 font-bold text-lg">
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        Pollar Ideation
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-6 px-6 font-semibold text-gray-900">
                        {row.feature}
                      </td>
                      <td className="py-6 px-6 bg-gray-50">
                        <div className="flex items-center justify-center gap-3 text-gray-600">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-center">{row.standard}</span>
                        </div>
                      </td>
                      <td className="py-6 px-6 bg-indigo-50/30">
                        <div className="flex items-center justify-center gap-3 text-gray-900 font-medium">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-center">{row.pollar}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-6">
              {comparisonRows.map((row, index) => (
                <Card key={index} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-4 text-gray-900">{row.feature}</h3>
                    
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-gray-500 mb-1">Basic Q&A Tools</div>
                            <div className="text-gray-600">{row.standard}</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-indigo-700 mb-1">Pollar</div>
                            <div className="text-gray-900 font-medium">{row.pollar}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Section C: The Tangible Difference */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                From a List of Questions to a Strategic Plan
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Slido gives you a list of 50 disconnected questions. Pollar identifies that 30 of those questions 
                are about "Resource Allocation" and automatically generates a summary of the team's concerns. 
                We turn curiosity into clarity.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Basic Q&A Output */}
              <Card className="border-2 border-gray-300 relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Basic Q&A
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-gray-500">Slido Output</h3>
                  
                  {/* Question List */}
                  <div className="bg-gray-50 rounded-lg p-6 min-h-[380px] space-y-3">
                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-700">Question #1</span>
                        <span className="text-xs text-gray-500">👍 12</span>
                      </div>
                      <p className="text-gray-600">Can we get more budget for tools?</p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-700">Question #2</span>
                        <span className="text-xs text-gray-500">👍 8</span>
                      </div>
                      <p className="text-gray-600">Who decides on resource allocation?</p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-700">Question #3</span>
                        <span className="text-xs text-gray-500">👍 15</span>
                      </div>
                      <p className="text-gray-600">Why are some teams better funded?</p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-700">Question #4</span>
                        <span className="text-xs text-gray-500">👍 6</span>
                      </div>
                      <p className="text-gray-600">When will hiring budget be approved?</p>
                    </div>
                    
                    <div className="text-center py-4">
                      <span className="text-gray-400 text-sm">... and 46 more questions</span>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>No pattern recognition</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Questions disconnected from each other</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>No sentiment analysis</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Facilitator must synthesize manually</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pollar's AI Output */}
              <Card className="border-2 border-indigo-500 relative overflow-hidden shadow-xl">
                <div className="absolute top-4 right-4 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  AI-Powered
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Pollar Output
                  </h3>
                  
                  {/* AI-Organized Themes */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 min-h-[380px]">
                    <div className="mb-4 bg-white rounded-lg p-4 border-l-4 border-indigo-600 shadow-sm">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-indigo-600 rounded w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">30</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">Resource Allocation Concerns</h4>
                          <div className="inline-flex items-center gap-2 text-xs">
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded">High Priority</span>
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded">😟 Frustrated</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
                        <strong>Summary:</strong> Team expresses concern about budget transparency and fairness 
                        in resource distribution across departments.
                      </p>
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <strong>Key Questions:</strong> Budget approval, allocation criteria, hiring timelines
                      </div>
                    </div>

                    <div className="mb-4 bg-white rounded-lg p-4 border-l-4 border-purple-600 shadow-sm">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-purple-600 rounded w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">12</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">Tool & Technology Stack</h4>
                          <div className="inline-flex items-center gap-2 text-xs">
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">Medium Priority</span>
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded">😊 Hopeful</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        <strong>Summary:</strong> Interest in upgrading development tools and modernizing tech infrastructure.
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border-l-4 border-blue-600 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-600 rounded w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">8</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">Process & Communication</h4>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Low Priority</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Questions clustered by theme</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Sentiment analysis included</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Priority levels identified</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">Executive summary auto-generated</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Context */}
            <div className="mt-12 max-w-4xl mx-auto">
              <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 border-0">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="hidden md:block">
                      <FileText className="w-16 h-16 text-white opacity-90" />
                    </div>
                    <div className="flex-1 text-white">
                      <h3 className="text-2xl font-bold mb-3">
                        Perfect for Leadership Presentations
                      </h3>
                      <p className="text-lg text-indigo-100 leading-relaxed">
                        When your CEO asks "What did the team say?", you don't hand them 50 questions. 
                        You present Pollar's AI summary: "30 questions about resource allocation, with concerns 
                        about transparency and fairness. Sentiment: Frustrated but engaged." That's the difference.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Use Case Scenarios */}
        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Built for Strategic Facilitators</h2>
              <p className="text-xl text-gray-600">
                When Q&A needs to connect to real outcomes
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Town Halls & All-Hands</h3>
                  <p className="text-gray-600 mb-4">
                    Turn 100+ employee questions into digestible themes for leadership follow-up.
                  </p>
                  <div className="text-sm text-indigo-600 font-medium">
                    → Sentiment tracking → Pattern detection
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Brain className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Strategic Planning</h3>
                  <p className="text-gray-600 mb-4">
                    Convert stakeholder questions into strategic priorities with AI-powered analysis.
                  </p>
                  <div className="text-sm text-indigo-600 font-medium">
                    → Theme clustering → Priority ranking
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-indigo-300 transition-colors">
                <CardContent className="p-6">
                  <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Multi-Session Projects</h3>
                  <p className="text-gray-600 mb-4">
                    Track evolving concerns across multiple meetings to show progress over time.
                  </p>
                  <div className="text-sm text-indigo-600 font-medium">
                    → Continuous tracking → Progress visualization
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section D: Final CTA */}
        <section className="py-20 px-6 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready for a Deeper Conversation?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Stop settling for surface-level Q&A. Pollar gives you the depth and continuity 
              that turns employee questions into strategic insights.
            </p>
            <Button 
              size="lg"
              onClick={() => handleCTA('final')}
              className="bg-white text-indigo-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto font-semibold"
            >
              Get Started with Pollar
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-indigo-100">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>AI analysis included</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Sentiment detection</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span>Executive summaries</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-400 py-12 px-6">
          <div className="container mx-auto max-w-6xl text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <PollarLogoSVG className="w-8 h-8" />
              <span className="text-xl font-bold text-white">Pollar</span>
            </div>
            <p className="mb-4">
              Q&A that goes beyond the question
            </p>
            <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
              <button onClick={() => navigate('/')} className="hover:text-white transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/alternatives/mentimeter-vs-pollar')} className="hover:text-white transition-colors">
                vs Mentimeter
              </button>
              <button onClick={() => navigate('/alternatives/miro-alternative-pollar')} className="hover:text-white transition-colors">
                vs Miro
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                Sign In
              </button>
              <button onClick={() => handleCTA('footer')} className="hover:text-white transition-colors">
                Get Started
              </button>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-sm">
              © {new Date().getFullYear()} Pollar. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SlidoAlternative;
